import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  QuickAddHandler,
  ResourceBoxProps,
  SeatBoxProps,
} from './incidentRosterChart.types';

const QuickAddMenu = ({
  positionId,
  seatName,
  onQuickAdd,
}: {
  positionId: string;
  seatName?: string;
  onQuickAdd: QuickAddHandler;
}) => {
  return (
    <div
      className="chartBox__quickAdd"
      role="button"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <StylishNewButton
              className="button--icon"
              title="Add position or resource"
              aria-label="Add position or resource"
            />
          }
        >
          <SharedIcon iconName="add" size="sm" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="left">
          <DropdownMenuItem
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onQuickAdd('position', positionId, seatName);
            }}
          >
            Add position
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onQuickAdd('resource', positionId, seatName);
            }}
          >
            Add resource
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const WorkTeamSeatBox = ({
  positionId,
  members,
  seatName,
  currentSection,
  setShowEditPositionModal,
  onQuickAdd,
}: SeatBoxProps) => {
  const leaders = members.filter(
    (member) =>
      member.incidentPositions.find((pos) => pos.positionId === positionId)
        ?.isLeader
  );
  const nonLeaders = members.filter(
    (member) =>
      !member.incidentPositions.find((pos) => pos.positionId === positionId)
        ?.isLeader
  );
  return (
    <div
      className={classNames('chartBox')}
      title={'Workgroup'}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        setShowEditPositionModal(positionId);
      }}
      style={{
        borderColor: currentSection?.borderColor || '#000',
        borderWidth: '3px',
      }}
    >
      <QuickAddMenu
        positionId={positionId}
        seatName={seatName}
        onQuickAdd={onQuickAdd}
      />
      {seatName && <div className="chartBox__role">{seatName}</div>}
      {leaders.length > 0 && nonLeaders.length > 0 && <div>Leader:</div>}
      {leaders.length === 1 ? (
        <div
          key={leaders[0].userId || leaders[0].incidentMemberId || `leader-0`}
          className="chartBox__person chartBox__person--leader"
        >
          {leaders[0].active === 1 && leaders[0].activeTime && <span>*</span>}
          {leaders[0].userName || leaders[0].emailAddress}
        </div>
      ) : (
        leaders.length > 0 && (
          <div
            key={`leaders`}
            className="chartBox__person chartBox__person--leader"
          >
            {leaders.length} LEADERS
          </div>
        )
      )}
      {leaders.length > 0 && nonLeaders.length > 0 && (
        <div className="chartBox__separator" />
      )}

      {nonLeaders.length === 1 ? (
        <div
          key={
            nonLeaders[0].userId || nonLeaders[0].incidentMemberId || `member-0`
          }
          className="chartBox__person"
        >
          {nonLeaders[0].active === 1 && nonLeaders[0].activeTime && (
            <span>*</span>
          )}
          {nonLeaders[0].userName || nonLeaders[0].emailAddress}
        </div>
      ) : (
        <div
          key={`nonLeaders-${nonLeaders.length}`}
          className="chartBox__person"
        >
          {nonLeaders.length} ASSIGNED
        </div>
      )}
    </div>
  );
};

export const SeatBox = ({
  positionId,
  members,
  seatName,
  currentSection,
  setShowEditPositionModal,
  onQuickAdd,
}: SeatBoxProps) => {
  let borderColor = '#000';

  if (currentSection) {
    borderColor = currentSection.borderColor;
  }

  if (seatName?.includes('Technical Specialist')) {
    borderColor = '#FFFF00';
  }

  return (
    <div
      className={classNames('chartBox')}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        setShowEditPositionModal(positionId);
      }}
      style={{
        borderColor,
        borderWidth: '3px',
      }}
    >
      <QuickAddMenu
        positionId={positionId}
        seatName={seatName}
        onQuickAdd={onQuickAdd}
      />
      {seatName && <div className="chartBox__role">{seatName}</div>}
      {members.length === 1 ? (
        <div
          key={members[0].userId || members[0].incidentMemberId || `member-0`}
          className="chartBox__person chartBox__person--leader"
        >
          {members[0].active === 1 && members[0].activeTime && <span>*</span>}
          {members[0].userName || members[0].emailAddress}
        </div>
      ) : (
        <div
          key={`members-${members.length}`}
          className="chartBox__person chartBox__person--leader"
        >
          {members.length} ASSIGNED
        </div>
      )}
    </div>
  );
};

export const buildSeatBox = (props: SeatBoxProps & { isWorkteam: boolean }) => {
  if (props.isWorkteam) {
    return <WorkTeamSeatBox {...props} />;
  } else {
    return <SeatBox {...props} />;
  }
};

export const ResourceBox: React.FC<ResourceBoxProps> = ({
  resource,
  onEdit,
}) => {
  return (
    <div
      className={classNames('chartBox', 'chartBox--resource')}
      role="button"
      onClick={(e) => {
        e.stopPropagation();
        onEdit(resource);
      }}
      style={{
        borderColor: '#7d3fb0',
        borderWidth: '3px',
      }}
    >
      <div className="chartBox__role">{resource.resourceSet.name}</div>
    </div>
  );
};
